<script lang="ts">
	import type { ContentBlock } from '$models/Block/ContentBlock';
	import { goto } from '$app/navigation';
	import { page as pageStore } from '$app/stores';
	import Button from '$components/UI/Buttons/Button.svelte';
	import { getWordPressImageSrcSet } from '$lib/images';
	import { getCartState } from '$lib/state/cart.svelte';

	// Component properties
	let { block }: { block: ContentBlock } = $props();
	const cartState = getCartState();

	// TODO: MAKE THE ADD TO CART BUNDLE BUTTON CONFIGURABLE IN WORDPRESS
	let onButtonClick = $derived.by(() => {
		if (block?.button?.url === '#add-to-cart-bundle') {
			return (event: MouseEvent) => {
				event.preventDefault();
				cartState.setCart({
					frequency: 8,
					products: [
						{
							id: '99968d2c-6371-4a7d-a336-c382995d5a38',
							quantity: 1
						},
						{
							id: '99968d2c-60ae-4be4-8ac7-e691cfc06d2d',
							quantity: 1
						},
						{
							id: '99968d2c-5c3e-40c2-b364-a758acf55fda',
							quantity: 1
						}
					],
					nonSubscribableProducts: [
						{
							id: '99968d2c-6741-4686-aa30-bdaaa752ca0e',
							quantity: 3
						}
					],
					couponCode: 'kortingscode'
				});
				goto($pageStore.data.page.layout.pages.cartPage);
			};
		}

		return null;
	});
</script>

<div class="content">
	<div
		class="content-container"
		class:no-image={!block?.image}
		class:is-wide={block.imageIsWide && block?.image}
		class:is-mobile-image-top={block?.mobileImageAlignment === 'top'}
		class:is-desktop-image-left={block?.desktopImageAlignment === 'left'}
	>
		<div class="content-inner">
			<div class="content-header">
				{#if block.subtitle}
					<div class="content-subtitle">{@html block.subtitle}</div>
				{/if}
				{#if block.title}
					<h2 class="content-title">{@html block.title}</h2>
				{/if}
			</div>
			{#if block?.text}
				<div class="content-text">
					<div class="rich-text">
						{@html block.text}
						{#if block?.button}
							<div class="content-button">
								{#if onButtonClick !== null}
									<Button
										type="anchor"
										href={block.button.url}
										variant={block.buttonClass}
										on:click={onButtonClick}>{block.button.title}</Button
									>
								{:else}
									<Button type="anchor" href={block.button.url} variant={block.buttonClass}
										>{block.button.title}</Button
									>
								{/if}
							</div>
						{/if}
					</div>
				</div>
			{/if}
		</div>
		{#if block?.image}
			<figure class="content-visual">
				<img
					src={block.image.url}
					srcset={getWordPressImageSrcSet(block.image)}
					sizes="(min-width: 768px) 50vw, 100vw"
					alt={block.image.alt}
					width={block.image.width}
					height={block.image.height}
					loading="lazy"
					decoding="async"
				/>
			</figure>
		{/if}
	</div>
</div>

<style lang="postcss">
	.content {
		&-container {
			display: grid;
			align-items: center;
			gap: 40px;
		}

		&-container.is-mobile-image-top &-inner {
			order: 2;
		}

		&-container.is-wide:not(.no-image) &-inner {
			padding-block: 40px;
		}

		&-container.is-wide &-visual {
			margin-inline: calc(var(--spacing-5) * -1); /* same as container padding */
		}

		&-container.is-wide &-visual img {
			border-radius: 0;
		}

		&-header {
			&:not(:last-child) {
				margin-bottom: 16px;
			}
		}

		&-subtitle,
		&-title {
			font-family: var(--font-secondary);
			color: var(--color-brown);
			text-transform: uppercase;
			line-height: 1;

			:global(.block.variant-dark) & {
				color: var(--color-white);
			}
		}

		&-subtitle {
			font-size: 14px;

			&:not(:last-child) {
				margin-bottom: 8px;
			}
		}

		&-title {
			font-size: 28px;

			&:not(:last-child) {
				margin-bottom: 8px;
			}
		}

		&-visual {
			img {
				display: block;
				max-width: 100%;
				height: auto;
				border-radius: 8px;
				overflow: hidden;
			}
		}

		@media (--md) {
			&-container {
				grid-template-columns: minmax(0, 0.5fr) minmax(0, 0.5fr);

				&.no-image {
					grid-template-columns: 1fr;
				}
			}

			&-container.is-mobile-image-top &-inner {
				order: unset;
			}

			&-container.is-desktop-image-left &-inner {
				order: 2;
			}

			&-container.is-wide:not(.no-image) &-inner {
				min-height: 460px;
				align-content: center;
				padding-block: 64px;
			}

			&-container.is-wide &-visual {
				position: relative;
				min-height: 480px;
				overflow: hidden;

				img {
					position: absolute;
					inset: 0;
					width: 100%;
					height: 100%;
					max-width: none;
					object-fit: cover;
				}
			}

			&-container.is-wide &-visual {
				margin: 0;
				height: 100%;
				margin-left: 0;
				margin-right: calc(-50vw + 50%);
			}

			&-container.is-wide.is-desktop-image-left &-visual {
				margin-right: 0;
				margin-left: calc(-50vw + 50%);
			}

			&-container.is-wide &-inner {
				padding-block: 40px;
			}

			&-text {
				margin-bottom: 0;
			}

			&-visual {
				img {
					border-radius: 12px;
				}
			}
		}

		@media (--lg) {
			&-container {
				gap: 64px;
			}

			&-visual {
				margin-left: 40px;
			}

			&-container.is-wide:not(.no-image) &-inner {
				padding-block: 80px;
			}

			&-container.is-desktop-image-left &-visual {
				margin-left: 0;
				margin-right: 40px;
			}
		}

		@media (--xl) {
			&-container {
				gap: 80px;
			}

			&-visual {
				margin-left: 80px;
			}

			&-container.is-wide:not(.no-image) &-inner {
				padding-block: 100px;
			}

			&-container.is-desktop-image-left &-visual {
				margin-left: 0;
				margin-right: 80px;
			}
		}
	}
</style>
